import React, { useEffect, useMemo, useRef, useState } from "react";
import { generateVisitDates } from "./udrzbaPlan";
import { apiFetch, apiJson } from "../../auth/authClient";

const API = "/api/udrzba";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  fontSize: 14,
  color: "#0f172a",
  background: "#ffffff",
  outline: "none",
  boxSizing: "border-box",
};





const galleryWrapStyle = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 10,
};

const galleryCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 8,
  background: "#fff",
};

function periodicitaLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "mesicne") return "Měsíčně";
  if (x === "ctvrtletne") return "Čtvrtletně";
  if (x === "pulrocne") return "Půlročně";
  if (x === "rocne") return "Ročně";
  if (x === "dle_domluvy") return "Dle domluvy";
  return v || "-";
}

function toIsoDateSafe(v) {
  const x = String(v || "").trim();
  if (!x) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(x)) return x;
  const d = new Date(x);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function makeIso(year, month, day) {
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

function shiftIsoDate(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m2 = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m2 + 114) / 31);
  const day = ((h + l - 7 * m2 + 114) % 31) + 1;
  return makeIso(year, month, day);
}

function nextAnnualDate(baseDate, fromIso) {
  const base = toIsoDateSafe(baseDate);
  const from = toIsoDateSafe(fromIso) || new Date().toISOString().slice(0, 10);
  if (!base) return "";
  const mmdd = base.slice(5);
  const year = Number(from.slice(0, 4));
  const candidate = `${year}-${mmdd}`;
  return candidate >= from ? candidate : `${year + 1}-${mmdd}`;
}

function uniqueSortedDates(items) {
  return Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((x) => toIsoDateSafe(x))
        .filter(Boolean)
    )
  ).sort();
}

function buildBalicekDates(form) {
  const refIso = toIsoDateSafe(form?.datumObjednani) || new Date().toISOString().slice(0, 10);
  const year = Number(refIso.slice(0, 4));
  const out = [];

  if (form?.balicekVelikonoce) {
    const easter = easterSunday(year);
    const velkyPatek = shiftIsoDate(easter, -2);
    if (velkyPatek) out.push(velkyPatek);
  }

  if (form?.balicekDusicky) out.push(makeIso(year, 11, 2));
  if (form?.balicekVanoce) out.push(makeIso(year, 12, 23));

  if (form?.balicekVyrociUmrti) {
    const x = nextAnnualDate(form?.balicekDatumUmrti || form?.datumUmrti, refIso);
    if (x) out.push(x);
  }

  if (form?.balicekNarozeniny) {
    const x = nextAnnualDate(form?.balicekDatumNarozeni || form?.datumNarozeni, refIso);
    if (x) out.push(x);
  }

  if (Array.isArray(form?.balicekVlastniData)) {
    out.push(...form.balicekVlastniData);
  }

  return uniqueSortedDates(out);
}

function balicekSummaryList(form) {
  const out = [];
  if (form?.balicekVelikonoce) out.push("Velikonoce");
  if (form?.balicekDusicky) out.push("Dušičky");
  if (form?.balicekVanoce) out.push("Vánoce");
  if (form?.balicekVyrociUmrti) out.push("Výročí úmrtí");
  if (form?.balicekNarozeniny) out.push("Narozeniny");
  if (Array.isArray(form?.balicekVlastniData) && form.balicekVlastniData.length) {
    out.push(`Vlastní termíny (${form.balicekVlastniData.length})`);
  }
  return out;
}

function buildBalicekVyznamneDny(form) {
  const refIso = toIsoDateSafe(form?.datumObjednani) || new Date().toISOString().slice(0, 10);
  const year = Number(refIso.slice(0, 4));
  const out = [];

  if (form?.balicekVelikonoce) {
    const easter = easterSunday(year);
    const velkyPatek = shiftIsoDate(easter, -2);
    if (velkyPatek) {
      out.push({ key: "velikonoce", label: "Velikonoce", datum: velkyPatek });
    }
  }

  if (form?.balicekDusicky) {
    out.push({ key: "dusicky", label: "Dušičky", datum: makeIso(year, 11, 2) });
  }

  if (form?.balicekVanoce) {
    out.push({ key: "vanoce", label: "Vánoce", datum: makeIso(year, 12, 23) });
  }

  if (form?.balicekVyrociUmrti) {
    const datum = nextAnnualDate(form?.balicekDatumUmrti || form?.datumUmrti, refIso);
    if (datum) out.push({ key: "vyroci_umrti", label: "Výročí úmrtí", datum });
  }

  if (form?.balicekNarozeniny) {
    const datum = nextAnnualDate(form?.balicekDatumNarozeni || form?.datumNarozeni, refIso);
    if (datum) out.push({ key: "narozeniny", label: "Narozeniny", datum });
  }

  return out.sort((a, b) => String(a?.datum || "").localeCompare(String(b?.datum || "")));
}

function buildPublicTerminyPraci(body = {}) {
  const terminy = Array.isArray(body?.balicekTerminy) ? body.balicekTerminy : [];
  const vyznamneDny = Array.isArray(body?.vyznamneDny) ? body.vyznamneDny : [];

  const vyznamnyDenMap = new Map(
    vyznamneDny
      .filter((x) => String(x?.datum || "").trim())
      .map((x) => [String(x.datum).slice(0, 10), x])
  );

  return terminy
    .map((datum, index) => {
      const iso = String(datum || "").slice(0, 10);
      if (!iso) return null;

      const poradi = index + 1;
      const vyznamnyDen = vyznamnyDenMap.get(iso);

      const jeBonusZdarma = poradi === 5;
      const jeJmeninova = !!vyznamnyDen && String(vyznamnyDen.key || "") === "narozeniny";

      let bonusTyp = "";
      let nazevNavstevy = `Návštěva ${poradi}`;
      let rezimNavstevy = "standard";
      let navrzenePolozky = [];

      if (jeBonusZdarma) {
        bonusTyp = "bonus_zdarma";
        nazevNavstevy = "5. bonusová návštěva zdarma";
      }

      if (jeJmeninova || poradi === 6) {
        bonusTyp = "jmeninova_bonusova";
        nazevNavstevy = "6. jmeninová bonusová návštěva";
        rezimNavstevy = "lehka_udrzba";
        navrzenePolozky = ["Svíčka", "Květina"];
      }

      return {
        id: `termin-${poradi}`,
        datum: iso,
        poradi,
        jeZdarma: jeBonusZdarma || bonusTyp === "jmeninova_bonusova",
        bonusTyp,
        nazevNavstevy,
        rezimNavstevy,
        navrzenePolozky,
        hlavniSluzba: String(body?.hlavniSluzba || "").trim(),
        typHrobu: String(body?.typHrobu || "").trim(),
        kvetinyLimit: String(body?.kvetinyLimit || "bez").trim(),
        dekoraceLimit: String(body?.dekoraceLimit || "bez").trim(),
        kvetinyCastka: Math.round(Number(body?.kvetinyCastka || 0) * 100) / 100,
        dekoraceCastka: Math.round(Number(body?.dekoraceCastka || 0) * 100) / 100,
      };
    })
    .filter(Boolean);
}


const sectionCardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const sectionTitleStyle = {
  fontWeight: 700,
  fontSize: 16,
  marginBottom: 8,
  color: "#0f172a",
};

const softBlockStyle = {
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 14,
};

const primaryButtonStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1.2,
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minHeight: 42,
  fontSize: 14,
  color: "#334155",
};

const chipBaseStyle = {
  padding: "6px 10px",
  borderRadius: 999,
  fontSize: 13,
  color: "#475569",
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};


const financniStavBadgeStyle = (v) => {
  const map = {
    nezaplaceno: {
      background: "#fff1f2",
      color: "#be123c",
      border: "1px solid #fecdd3",
    },
    zalohovano: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    zaplaceno: {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #86efac",
    },
  };

  const cfg = map[String(v || "").trim().toLowerCase()] || {
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
  };

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 28,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
    background: cfg.background,
    color: cfg.color,
    border: cfg.border,
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  };
};


function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ""));
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

export default function UdrzbaNovaZakazka({ setSection }) {
  const [cenik, setCenik] = useState([]);
  const [uploadingPred, setUploadingPred] = useState(false);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [saving, setSaving] = useState(false);
  const predRef = useRef(null);
  const poRef = useRef(null);

  function getEmptyForm() {
    return {
      objednavatel: "",
      telefon: "",
      email: "",
      cisloHrobu: "",
      oddeleni: "",
      skupina: "",
      lokaceHrobu: "",
      majitelHrobuJmeno: "",
      zesnulyPrijmeni: "",
      zesnulyDatumUmrti: "",
      jmenoNaDesce: "",
      identifikaceText: "",
      hrbitov: "Český Krumlov",
      datumObjednani: new Date().toISOString().slice(0, 10),
      planovaneDatum: "",
      datumZhotoveni: "",
      dalsiNavsteva: "",
      posledniNavsteva: "",
      uhrazenoDo: "",
      typPece: "",
      pocetNavstevCelkem: 0,
      zbyvaNavstev: 0,
      stavPredplatneho: "aktivni",
      cenaCelkem: 0,
      zalohaCastka: 0,
      doplatekCastka: 0,
      uhrazenoCelkem: 0,
      financniStav: "nezaplaceno",
      zadaniPrace: "",
      poznamka: "",
      stav: "nova",
      zaplaceno: false,
      opakovana: false,
      periodicita: "",
      automatickyZapsatDoKalendare: false,
      hlavniSluzbaId: "",
      fotoPred: [],
      fotoPo: [],
      items: [],
      kvetinyLimit: "bez",
      dekoraceLimit: "bez",
      kvetinyCastka: 0,
      dekoraceCastka: 0,
      kvetinyVlastniCastka: "",
      dekoraceVlastniCastka: "",
    };
  }

  const [form, setForm] = useState(getEmptyForm());

  useEffect(() => {
    apiJson(`${API}/cenik`, { method: "GET" })
      .then((d) => setCenik(Array.isArray(d?.items) ? d.items : []))
      .catch(() => setCenik([]));
  }, []);

  const grouped = useMemo(() => {
    const m = {};
    cenik.forEach((it) => {
      const k = it.skupina || "Ostatní";
      if (!m[k]) m[k] = [];
      m[k].push(it);
    });
    return m;
  }, [cenik]);

  const addItem = (it) => {
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `${it.id}_${Date.now()}`,
          cenikId: it.id,
          nazev: it.nazev,
          skupina: it.skupina,
          jednotka: it.jednotka || "zakázka",
          mnozstvi: 1,
          cenaBezDph: Number(it.cenaBezDph || 0),
          dph: Number(it.dph || 21),
        },
      ],
    }));
  };

  const updateItem = (id, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  };

  const removeItem = (id) => {
    setForm((prev) => {
      const currentItems = Array.isArray(prev.items) ? prev.items : [];
      const removed = currentItems.find((x) => x.id === id);
      const nextItems = currentItems.filter((x) => x.id !== id);
      const removedWasMain =
        String(removed?.cenikId || "") === String(prev.hlavniSluzbaId || "");

      return {
        ...prev,
        hlavniSluzbaId: removedWasMain ? "" : prev.hlavniSluzbaId,
        items: nextItems,
      };
    });
  };
  const removeFoto = (kind, url) => {
    setForm((prev) => ({
      ...prev,
      [kind]: (prev[kind] || []).filter((x) => x !== url),
    }));
  };

  const onPickPred = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 10) {
      alert("Najednou můžeš nahrát maximálně 10 fotek.");
      if (poRef.current) poRef.current.value = "";
      return;
    }
    if (files.length > 10) {
      alert("Najednou můžeš nahrát maximálně 10 fotek.");
      if (predRef.current) predRef.current.value = "";
      return;
    }
    try {
      setUploadingPred(true);
      const urls = [];
      for (const f of files) {
        if (!f || !String(f.type || "").startsWith("image/")) {
          throw new Error("Vybraný soubor není obrázek.");
        }
        const dataUrl = await fileToDataUrl(f);
        const res = await apiFetch(`/api/foto/upload`, {
          method: "POST",
          body: {
            filename: f.name || "foto.jpg",
            dataUrl,
            kind: "pred",
          },
        });
        const data = await res.json();
        if (!data?.ok || !data?.url) throw new Error(data?.error || "Upload selhal");
        urls.push(data.url);
      }
      setForm((prev) => ({ ...prev, fotoPred: [...(prev.fotoPred || []), ...urls] }));
    } catch (err) {
      alert(err?.message || "Nahrání fotek před selhalo.");
    } finally {
      setUploadingPred(false);
      if (predRef.current) predRef.current.value = "";
    }
  };

  const onPickPo = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      setUploadingPo(true);
      const urls = [];
      for (const f of files) {
        if (!f || !String(f.type || "").startsWith("image/")) {
          throw new Error("Vybraný soubor není obrázek.");
        }
        const dataUrl = await fileToDataUrl(f);
        const res = await apiFetch(`/api/foto/upload`, {
          method: "POST",
          body: {
            filename: f.name || "foto.jpg",
            dataUrl,
            kind: "po",
          },
        });
        const data = await res.json();
        if (!data?.ok || !data?.url) throw new Error(data?.error || "Upload selhal");
        urls.push(data.url);
      }
      setForm((prev) => ({ ...prev, fotoPo: [...(prev.fotoPo || []), ...urls] }));
    } catch (err) {
      alert(err?.message || "Nahrání fotek po selhalo.");
    } finally {
      setUploadingPo(false);
      if (poRef.current) poRef.current.value = "";
    }
  };

  const save = async () => {
    if (saving) return;

    const errors = validateForm(form, isJednorazova, isPredplatne, isBalicek, cenaCelkemNum, uhrazenoCelkemNum);


    if (errors.length) {
      alert(errors.join("\n"));
      return;
    }

    setSaving(true);
    try {
        const payloadItems = (Array.isArray(form.items) ? form.items : []).map((it) => {
          const isMain = String(it.cenikId || "") === String(form.hlavniSluzbaId || "");
          const execCount = isMain ? visitMultiplier : getExecCountForItem(it);

          let terminy = [];
          if (isMain) {
            terminy = isJednorazova
              ? (String(form.planovaneDatum || "").trim() ? [String(form.planovaneDatum || "").trim()] : [])
              : (Array.isArray(generatedVisitDates) ? generatedVisitDates : []);
          } else if (isJednorazova) {
            terminy = String(form.planovaneDatum || "").trim() ? [String(form.planovaneDatum || "").trim()] : [];
          } else if (String(it.rezimUctovani || "jednou") === "kazda_navsteva") {
            terminy = Array.isArray(generatedVisitDates) ? generatedVisitDates : [];
          } else if (String(it.rezimUctovani || "jednou") === "vybrane_navstevy") {
            terminy = Array.isArray(it.vybraneNavstevy) ? it.vybraneNavstevy : [];
          } else {
            terminy = (Array.isArray(generatedVisitDates) && generatedVisitDates.length)
              ? [generatedVisitDates[0]]
              : [];
          }

          return {
            ...it,
            rezimUctovani: isMain ? "hlavni" : String(it.rezimUctovani || "jednou"),
            vybraneNavstevy: Array.isArray(it.vybraneNavstevy) ? it.vybraneNavstevy : [],
            execCount,
            terminyText: terminy.join(", "),
          };
        });

        const lokaceHrobu = [form.oddeleni, form.skupina, form.cisloHrobu]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(" / ");

        const identifikaceText = [
          String(form.majitelHrobuJmeno || "").trim() ? `Majitel: ${String(form.majitelHrobuJmeno || "").trim()}` : "",
          (String(form.zesnulyPrijmeni || "").trim() || String(form.zesnulyDatumUmrti || "").trim())
            ? `Zesnulý: ${[String(form.zesnulyPrijmeni || "").trim(), String(form.zesnulyDatumUmrti || "").trim()].filter(Boolean).join(" / ")}`
            : "",
          String(form.jmenoNaDesce || "").trim() ? `Deska: ${String(form.jmenoNaDesce || "").trim()}` : ""
        ].filter(Boolean).join(" • ");

        const payload = {
          ...form,
          lokaceHrobu,
          identifikaceText,
          items: payloadItems,
          hlavniSluzba: String(form.hlavniSluzba || "").trim(),
          hlavniSluzbaId: String(form.hlavniSluzbaId || "").trim(),
          typHrobu: String(form.typHrobu || "").trim(),
          kvetinyLimit: String(form.kvetinyLimit || "bez").trim(),
          dekoraceLimit: String(form.dekoraceLimit || "bez").trim(),
          kvetinyCastka: Math.round(Number(form.kvetinyCastka || 0) * 100) / 100,
          dekoraceCastka: Math.round(Number(form.dekoraceCastka || 0) * 100) / 100,
          planovaneDatum: isJednorazova ? String(form.planovaneDatum || "").trim() : String(generatedVisitDates[0] || ""),
          datumObjednani: String(form.datumObjednani || new Date().toISOString().slice(0, 10)).trim(),
          datumZhotoveni: String(form.datumZhotoveni || "").trim(),
          dalsiNavsteva: isJednorazova ? "" : String(generatedVisitDates[0] || ""),
          datumOd: isJednorazova ? "" : String(form.datumOd || "").trim(),
          datumDo: isJednorazova ? "" : String(form.datumDo || "").trim(),
          planNavstev: isJednorazova ? [] : generatedVisitDates,
          balicekTerminy: isBalicek ? generatedVisitDates : [],
          balicekSvatky: isBalicek
            ? [
                form.balicekVelikonoce ? "Velikonoce" : "",
                form.balicekDusicky ? "Dušičky" : "",
                form.balicekVanoce ? "Vánoce" : "",
                form.balicekVyrociUmrti ? "Výročí úmrtí" : "",
                form.balicekNarozeniny ? "Narozeniny" : "",
              ].filter(Boolean)
            : [],
          vyznamneDny: isBalicek ? buildBalicekVyznamneDny(form) : [],
          terminyPraci: isBalicek ? buildPublicTerminyPraci({
            hlavniSluzba: String(form.hlavniSluzba || "").trim(),
            typHrobu: String(form.typHrobu || "").trim(),
            kvetinyLimit: String(form.kvetinyLimit || "bez").trim(),
            dekoraceLimit: String(form.dekoraceLimit || "bez").trim(),
            kvetinyCastka: Math.round(Number(form.kvetinyCastka || 0) * 100) / 100,
            dekoraceCastka: Math.round(Number(form.dekoraceCastka || 0) * 100) / 100,
            balicekTerminy: generatedVisitDates,
            vyznamneDny: buildBalicekVyznamneDny(form),
          }) : [],
          posledniNavsteva: String(form.posledniNavsteva || "").trim(),
          uhrazenoDo: isPredplatne ? String(form.uhrazenoDo || "").trim() : "",
          periodicita: isJednorazova ? "" : String(form.periodicita || "").trim(),
          opakovana: isJednorazova ? false : true,
          pocetNavstevCelkem: isBalicek ? generatedVisitDates.length : (isJednorazova ? 1 : 0),
          zbyvaNavstev: isBalicek ? generatedVisitDates.length : (isJednorazova ? 1 : 0),
          stavPredplatneho: isJednorazova ? "" : String(form.stavPredplatneho || "aktivni"),
          cenaCelkem: cenaCelkemNum,
          zalohaCastka: zalohaCastkaNum,
          doplatekCastka: doplatekCastkaNum,
          uhrazenoCelkem: uhrazenoCelkemNum,
        };
      const data = await apiJson(`${API}/zakazky`, {
        method: "POST",
        body: payload,
      });

      if (data?.ok) {
        try {
          if (payload.automatickyZapsatDoKalendare && data?.item?.id) {
            await apiJson(`${API}/kalendar/vytvor-zakazka/${data.item.id}`, {
              method: "POST",
              body: {},
            });
          }
        } catch (_) {}

        alert(`Zakázka uložena: ${data.item.id}`);
        try { sessionStorage.setItem("pohrebka_udrzba_last_created_id", String(data.item.id || "")); } catch {}
        setForm(getEmptyForm());
        if (typeof setSection === "function") setSection("zakazky");
      } else {
        alert(data?.error || "Uložení selhalo");
      }
    } finally {
      setSaving(false);
    }
  };

  const isJednorazova = String(form.typPece || "") === "jednorazova";
  const isPredplatne = String(form.typPece || "") === "predplatne";
  const isBalicek = String(form.typPece || "") === "balicek";

  const generatedVisitDates = useMemo(() => {
    if (isJednorazova) return [];

    if (isBalicek) {
      return buildBalicekDates(form);
    }

    return generateVisitDates({
      datumOd: form.datumOd,
      datumDo: form.datumDo,
      periodicita: form.periodicita,
    });
  }, [
    form.datumOd,
    form.datumDo,
    form.periodicita,
    form.balicekVelikonoce,
    form.balicekDusicky,
    form.balicekVanoce,
    form.balicekVyrociUmrti,
    form.balicekNarozeniny,
    form.balicekDatumUmrti,
    form.balicekDatumNarozeni,
    form.balicekVlastniData,
    isJednorazova,
    isBalicek,
  ]);

  const includedBalicekVisits = 4;
  const visitMultiplier = isJednorazova ? 1 : Math.max(0, generatedVisitDates.length);
  const balicekVisitCount = Math.max(0, generatedVisitDates.length);
  const balicekExtraVisits = isBalicek ? Math.max(0, balicekVisitCount - includedBalicekVisits) : 0;

  const getAllowedVisitDatesForItem = (item) => {
    if (isJednorazova) {
      return String(form.planovaneDatum || "").trim() ? [String(form.planovaneDatum || "").trim()] : [];
    }
    return Array.isArray(generatedVisitDates) ? generatedVisitDates : [];
  };

  const getExecCountForItem = (item) => {
    if (!item) return 0;

    const isMain = String(item.cenikId || "") === String(form.hlavniSluzbaId || "");
    if (isMain) {
      return isJednorazova ? 1 : Math.max(0, generatedVisitDates.length);
    }

    const allowedDates = getAllowedVisitDatesForItem(item);
    const mode = String(item.rezimUctovani || "jednou");

    if (isJednorazova) return 1;
    if (mode === "kazda_navsteva") return allowedDates.length;
    if (mode === "vybrane_navstevy") {
      const selected = Array.isArray(item.vybraneNavstevy) ? item.vybraneNavstevy : [];
      return selected.filter((d) => allowedDates.includes(d)).length;
    }
    return allowedDates.length > 0 ? 1 : 0;
  };

  const isDoplnkovaSluzba = (item) => {
    const id = String(item?.id || "");
    const skupina = String(item?.skupina || "").toLowerCase();
    const nazev = String(item?.nazev || "").toLowerCase();

    return (
      skupina.includes("další služby") ||
      skupina.includes("dalsi sluzby") ||
      id === "jine" ||
      nazev.includes("fotodokumentace") ||
      nazev.includes("zapálení svíčky") ||
      nazev.includes("zapaleni svicky") ||
      nazev.includes("výsadba květin") ||
      nazev.includes("vysadba kvetin") ||
      nazev.includes("mytí náhrobní desky") ||
      nazev.includes("myti nahrobni desky")
    );
  };

  const isAllowedMainService = (item) => {
    if (!item || isDoplnkovaSluzba(item)) return false;

    const skupina = String(item?.skupina || "").toLowerCase();

    if (isJednorazova) {
      const rezim = String(form.jednorazovyRezim || "");

      if (rezim === "cisteni") {
        return (
          skupina.includes("jednorázové čištění") ||
          skupina.includes("jednorazove cisteni")
        );
      }

      if (rezim === "obnova") {
        return (
          skupina.includes("kompletní obnova") ||
          skupina.includes("kompletni obnova")
        );
      }

      return false;
    }

    if (isPredplatne) {
      return skupina.includes("pravidelná péče") || skupina.includes("pravidelna pece");
    }

    if (isBalicek) {
      return skupina.includes("roční balíček") || skupina.includes("rocni balicek");
    }

    return false;
  };

  const hlavniSluzby = (Array.isArray(cenik) ? cenik : []).filter(isAllowedMainService);
  const doplnkoveSluzby = (Array.isArray(cenik) ? cenik : []).filter(isDoplnkovaSluzba);

  const mainServiceItem = (Array.isArray(form.items) ? form.items : []).find(
    (x) => String(x?.cenikId || "") === String(form.hlavniSluzbaId || "")
  ) || null;

  const doplnkovePolozky = (Array.isArray(form.items) ? form.items : []).filter(
    (x) => String(x?.cenikId || "") !== String(form.hlavniSluzbaId || "")
  );


  const applyBalicekVisualSelection = (prevForm, kind, value) => {
    const isKvetiny = kind === "kvetiny";
    const cenikId = isKvetiny ? "__balicek_kvetiny__" : "__balicek_dekorace__";

    const castkaMap = isKvetiny
        ? {
            bez: 0,
            do_300: 300,
            do_500: 500,
            vlastni: Number(prevForm.kvetinyVlastniCastka || 0),
          }
        : {
            bez: 0,
            do_200: 200,
            do_300: 300,
            vlastni: Number(prevForm.dekoraceVlastniCastka || 0),
          };

    const labelMap = isKvetiny
      ? {
          bez: "",
          do_300: "Květiny do 300 Kč v rámci balíčku",
          do_500: "Květiny do 500 Kč v rámci balíčku",
          vlastni: "Květiny – vlastní částka v rámci balíčku",
        }
      : {
          bez: "",
          do_200: "Dekorace do 200 Kč v rámci balíčku",
          do_300: "Dekorace do 300 Kč v rámci balíčku",
          vlastni: "Dekorace – vlastní částka v rámci balíčku",
        };

    const nextLimit = String(value || "bez");
    const nextCastka = Number(castkaMap[nextLimit] || 0);

    const nextItems = (Array.isArray(prevForm.items) ? prevForm.items : []).filter(
      (x) => String(x?.cenikId || "") !== cenikId
    );

    if (nextLimit !== "bez" && String(labelMap[nextLimit] || "").trim()) {
      nextItems.push({
        id: `${cenikId}_${Date.now()}`,
        cenikId,
        nazev: String(labelMap[nextLimit] || "").trim(),
        skupina: "Balíček – limity péče",
        jednotka: "balíček",
        mnozstvi: 1,
        cenaBezDph: nextCastka,
        dph: 21,
        rezimUctovani: "jednou",
        vybraneNavstevy: [],
        jeBalicekVizual: true,
      });
    }

    return {
      ...prevForm,
      [isKvetiny ? "kvetinyLimit" : "dekoraceLimit"]: nextLimit,
      [isKvetiny ? "kvetinyCastka" : "dekoraceCastka"]: nextCastka,
      items: nextItems,
    };
  };

  const setMainService = (it) => {
    const prevItems = Array.isArray(form.items) ? form.items : [];
    const prevDoplnky = prevItems.filter((x) => String(x?.cenikId || "") !== String(form.hlavniSluzbaId || ""));
      const base = {
        id: `${it.id}_${Date.now()}`,
        cenikId: it.id,
        nazev: it.nazev,
        skupina: it.skupina || "",
        jednotka: it.jednotka || "zakázka",
        mnozstvi: 1,
        cenaBezDph: Number(it.cenaBezDph || 0),
        dph: Number(it.dph || 21),
        rezimUctovani: "jednou",
        vybraneNavstevy: [],
      };

    setForm({
      ...form,
      hlavniSluzbaId: String(it.id || ""),
      items: [base, ...prevDoplnky],
    });
  };

  const toggleDoplnkova = (it) => {
    const prevItems = Array.isArray(form.items) ? form.items : [];
    const exists = prevItems.some((x) => String(x?.cenikId || "") === String(it.id || ""));
    if (exists) {
      setForm({
        ...form,
        items: prevItems.filter((x) => String(x?.cenikId || "") !== String(it.id || "")),
      });
      return;
    }

      const base = {
        id: `${it.id}_${Date.now()}`,
        cenikId: it.id,
        nazev: it.nazev,
        skupina: it.skupina || "",
        jednotka: it.jednotka || "zakázka",
        mnozstvi: 1,
        cenaBezDph: Number(it.cenaBezDph || 0),
        dph: Number(it.dph || 21),
        rezimUctovani: "jednou",
        vybraneNavstevy: [],
      };

    setForm({
      ...form,
      items: [...prevItems, base],
    });
  };

    const cenaCelkemNum = Math.round(
      (
        (Array.isArray(form.items) ? form.items : []).reduce((sum, item) => {
          if (!item) return sum;

          const mnozstvi = Number(item.mnozstvi || 0);
          const cenaBezDph = Number(item.cenaBezDph || 0);
          const dph = Number(item.dph || 0);
          const isMain = String(item.cenikId || "") === String(form.hlavniSluzbaId || "");

          if (isMain && isBalicek) {
            return sum + (mnozstvi * cenaBezDph * (1 + dph / 100));
          }

          const execCount = isMain ? visitMultiplier : getExecCountForItem(item);
          const radekCelkem = mnozstvi * cenaBezDph * (1 + dph / 100) * Math.max(0, execCount);
          return sum + radekCelkem;
        }, 0)
        +
        (() => {
          if (!isBalicek || !mainServiceItem) return 0;

          const base = Number(mainServiceItem.cenaBezDph || 0);
          const dph = Number(mainServiceItem.dph || 0);
          const extraUnitBezDph = base / includedBalicekVisits;

          return balicekExtraVisits * extraUnitBezDph * (1 + dph / 100);
        })()
      ) * 100
    ) / 100;

  const zalohaCastkaNum = Math.round(Number(form.zalohaCastka || 0) * 100) / 100;
  const uhrazenoCelkemNum = Math.round(Number(form.uhrazenoCelkem || 0) * 100) / 100;
  const doplatekCastkaNum = Math.max(0, Math.round((cenaCelkemNum - uhrazenoCelkemNum) * 100) / 100);

  function validateForm(form, isJednorazova, isPredplatne, isBalicek, cenaCelkemNum, uhrazenoCelkemNum) {
    const errors = [];

    if (!String(form.objednavatel || "").trim()) {
      errors.push("Vyplň jméno objednavatele.");
    }

    if (!String(form.hrbitov || "").trim()) {
      errors.push("Vyplň hřbitov.");
    }

    const hasPresnaLokace =
          !!String(form.cisloHrobu || "").trim() ||
          !!String(form.oddeleni || "").trim() ||
          !!String(form.skupina || "").trim();

        const hasNahradniIdentifikace =
          !!String(form.majitelHrobuJmeno || "").trim() ||
          !!String(form.jmenoNaDesce || "").trim() ||
          !!String(form.zesnulyPrijmeni || "").trim() ||
          !!String(form.zesnulyDatumUmrti || "").trim();

        if (!hasPresnaLokace && !hasNahradniIdentifikace) {
          errors.push("Vyplň číslo hrobu nebo údaje pro dohledání hrobu.");
        }

    if (!String(form.typPece || "").trim()) {
      errors.push("Vyber typ péče.");
    }

      if (isJednorazova) {
        if (!String(form.planovaneDatum || "").trim()) {
          errors.push("Vyplň nejbližší plánovanou návštěvu.");
        }
      }

      if (isJednorazova && !String(form.jednorazovyRezim || "").trim()) {
        errors.push("U jednorázové péče vyber režim: čištění nebo obnova.");
      }

      if (isPredplatne && !String(form.periodicita || "").trim()) {
        errors.push("Vyber periodicitu.");
      }

      if (!isJednorazova && generatedVisitDates.length <= 0) {
        errors.push(
          isBalicek
            ? "U balíčku vyber alespoň jeden termín nebo přidej vlastní datum."
            : "Pro opakovanou péči musí vycházet alespoň jeden termín návštěvy."
        );
      }

    if (isPredplatne && !String(form.uhrazenoDo || "").trim()) {
      errors.push("U předplatného vyplň datum uhrazeno do.");
    }

      if (isBalicek) {
        const celkem = Math.max(0, generatedVisitDates.length);

        if (celkem <= 0) {
          errors.push("U balíčku vyber alespoň jeden skutečný termín návštěvy.");
        }
      }

    if (Number(uhrazenoCelkemNum || 0) < 0) {
      errors.push("Uhrazeno celkem nemůže být záporné.");
    }

    if (Number(cenaCelkemNum || 0) < 0) {
      errors.push("Cena celkem nemůže být záporná.");
    }

    const hasItems = Array.isArray(form.items) && form.items.length > 0;
    const hasZadani = !!String(form.zadaniPrace || "").trim();

      if (String(form.typPece || "").trim() && !String(form.hlavniSluzbaId || "").trim()) {
        errors.push("Vyber jednu hlavní službu podle typu péče.");
      }

    if (!hasItems && !hasZadani) {
      errors.push("Vyber alespoň jednu službu z ceníku nebo vyplň zadání práce.");
    }

    return errors;
  }

  const financniStavAuto =
    uhrazenoCelkemNum <= 0
      ? "nezaplaceno"
      : (cenaCelkemNum > 0 && uhrazenoCelkemNum + 0.001 < cenaCelkemNum)
        ? "zalohovano"
        : "zaplaceno";

  const nextPlanDate = isJednorazova
    ? String(form.planovaneDatum || "").trim()
    : String(generatedVisitDates[0] || "").trim();

  const workflowSummaryType = isJednorazova
    ? `Jednorázová • ${String(form.jednorazovyRezim || "").trim() === "obnova" ? "obnova" : (String(form.jednorazovyRezim || "").trim() === "cisteni" ? "čištění" : "bez režimu")}`
    : isPredplatne
      ? `Předplatné • ${periodicitaLabel(form.periodicita)}`
      : isBalicek
        ? `Balíček • ${balicekSummaryList(form).join(", ") || "bez termínů"}`
        : "Nezvoleno";

  const workflowSummaryNext = !String(form.typPece || "").trim()
    ? "Vyber typ péče."
    : !String(form.hlavniSluzbaId || "").trim()
      ? "Vyber hlavní službu."
        : isJednorazova
          ? "Po uložení vznikne jedna zakázka s jedním termínem."
          : isBalicek
            ? `Po uložení vznikne balíček ${generatedVisitDates.length} návštěv podle zvolených svátků a termínů.`
            : `Po uložení vznikne plán ${generatedVisitDates.length} návštěv a nastaví se nejbližší termín.`;


    return (
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 26, color: "#0f172a", fontWeight: 800 }}>Nová zakázka</h2>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>Základní údaje</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Jméno objednavatele</div>
                <input style={inputStyle} value={form.objednavatel} onChange={(e) => setForm({ ...form, objednavatel: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Telefon</div>
                <input style={inputStyle} value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>E-mail</div>
                <input style={inputStyle} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Číslo hrobu</div>
                <input style={inputStyle} value={form.cisloHrobu} onChange={(e) => setForm({ ...form, cisloHrobu: e.target.value })} />

                <div style={{ gridColumn: "1 / -1", marginTop: 10, padding: 12, border: "1px solid #e5e7eb", borderRadius: 12, background: "#f8fafc" }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: "#0f172a" }}>Upřesnění hrobu / dohledání</div>
                  <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
                    Když zákazník nezná přesné číslo hrobu, vyplň aspoň majitele hrobu, příjmení zesnulého, datum/rok úmrtí nebo jméno na desce.
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Oddělení</div>
                      <input style={inputStyle} value={form.oddeleni || ""} onChange={(e) => setForm({ ...form, oddeleni: e.target.value })} />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Skupina</div>
                      <input style={inputStyle} value={form.skupina || ""} onChange={(e) => setForm({ ...form, skupina: e.target.value })} />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Majitel hrobu</div>
                      <input style={inputStyle} value={form.majitelHrobuJmeno || ""} onChange={(e) => setForm({ ...form, majitelHrobuJmeno: e.target.value })} />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Příjmení zesnulého</div>
                      <input style={inputStyle} value={form.zesnulyPrijmeni || ""} onChange={(e) => setForm({ ...form, zesnulyPrijmeni: e.target.value })} />
                    </div>

                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum / rok úmrtí</div>
                      <input style={inputStyle} value={form.zesnulyDatumUmrti || ""} onChange={(e) => setForm({ ...form, zesnulyDatumUmrti: e.target.value })} placeholder="např. 2020 nebo 12.03.2020" />
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Jméno na nápisové desce</div>
                      <input style={inputStyle} value={form.jmenoNaDesce || ""} onChange={(e) => setForm({ ...form, jmenoNaDesce: e.target.value })} placeholder="stačí jedno jméno pro kontrolu" />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Hřbitov</div>
                <input style={inputStyle} value={form.hrbitov} onChange={(e) => setForm({ ...form, hrbitov: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum objednání</div>
                <input style={inputStyle} type="date" value={form.datumObjednani} onChange={(e) => setForm({ ...form, datumObjednani: e.target.value })} />
              </div>
                {isJednorazova ? (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Nejbližší plánovaná návštěva</div>
                    <input
                      style={inputStyle}
                      type="date"
                      value={form.planovaneDatum}
                      onChange={(e) => setForm({ ...form, planovaneDatum: e.target.value })}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Nejbližší plánovaná návštěva</div>
                    <input
                      style={{ ...inputStyle, background: "#f8fafc" }}
                      type="date"
                      value={generatedVisitDates[0] || ""}
                      readOnly
                    />
                  </div>
                )}
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum posledního dokončení</div>
                <input style={inputStyle} type="date" value={form.datumZhotoveni} onChange={(e) => setForm({ ...form, datumZhotoveni: e.target.value })} />
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div style={sectionTitleStyle}>Péče a návaznosti</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Typ péče</div>
                <select
                  style={inputStyle}
                  value={form.typPece || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const isBalicekNext = v === "balicek";
                      const isPredplatneNext = v === "predplatne";
                      setForm({
                        ...form,
                        typPece: v,
                        opakovana: isBalicekNext || isPredplatneNext,
                        pocetNavstevCelkem: 0,
                        zbyvaNavstev: 0,

                        stavPredplatneho: "aktivni",
                        dalsiNavsteva: v === "jednorazova" ? "" : form.dalsiNavsteva,
                        uhrazenoDo: isPredplatneNext ? form.uhrazenoDo : "",
                        periodicita: v === "jednorazova" || isBalicekNext ? "" : form.periodicita,
                        datumOd: isBalicekNext ? "" : form.datumOd,
                        datumDo: isBalicekNext ? "" : form.datumDo,
                        balicekVelikonoce: isBalicekNext ? !!form.balicekVelikonoce : false,
                        balicekDusicky: isBalicekNext ? !!form.balicekDusicky : false,
                        balicekVanoce: isBalicekNext ? !!form.balicekVanoce : false,
                        balicekVyrociUmrti: isBalicekNext ? !!form.balicekVyrociUmrti : false,
                        balicekNarozeniny: isBalicekNext ? !!form.balicekNarozeniny : false,
                        balicekDatumUmrti: isBalicekNext ? (form.balicekDatumUmrti || "") : "",
                        balicekDatumNarozeni: isBalicekNext ? (form.balicekDatumNarozeni || "") : "",
                        balicekVlastniData: isBalicekNext ? (Array.isArray(form.balicekVlastniData) ? form.balicekVlastniData : []) : [],
                        noveBalicekDatum: isBalicekNext ? (form.noveBalicekDatum || "") : "",
                        hlavniSluzbaId: "",
                        items: [],
                      });
                    }}
                >
                  <option value="">Vyber typ péče</option>
                  <option value="jednorazova">Jednorázová</option>
                  <option value="predplatne">Předplatné podle času</option>
                  <option value="balicek">Balíček návštěv</option>
                </select>
              </div>

                {isJednorazova ? (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Režim jednorázové péče</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, jednorazovyRezim: "cisteni", hlavniSluzbaId: "", items: [] })}
                        style={{
                          ...primaryButtonStyle,
                          background: form.jednorazovyRezim === "cisteni" ? "#0f172a" : "#ffffff",
                          color: form.jednorazovyRezim === "cisteni" ? "#ffffff" : "#0f172a",
                          border: form.jednorazovyRezim === "cisteni" ? "1px solid #0f172a" : "1px solid #cbd5e1",
                        }}
                      >
                        Jednorázové čištění
                      </button>

                      <button
                        type="button"
                        onClick={() => setForm({ ...form, jednorazovyRezim: "obnova", hlavniSluzbaId: "", items: [] })}
                        style={{
                          ...primaryButtonStyle,
                          background: form.jednorazovyRezim === "obnova" ? "#0f172a" : "#ffffff",
                          color: form.jednorazovyRezim === "obnova" ? "#ffffff" : "#0f172a",
                          border: form.jednorazovyRezim === "obnova" ? "1px solid #0f172a" : "1px solid #cbd5e1",
                        }}
                      >
                        Kompletní obnova
                      </button>
                    </div>
                  </div>
                ) : (
                  <div />
                )}

                {!isJednorazova ? (
                  !isBalicek ? (
                    <div>
                      <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Periodicita</div>
                      <select
                        style={inputStyle}
                        value={form.periodicita || ""}
                        onChange={(e) => setForm({ ...form, periodicita: e.target.value })}
                      >
                        <option value="">Vyber periodicitu</option>
                        <option value="mesicne">Měsíčně</option>
                        <option value="ctvrtletne">Čtvrtletně</option>
                        <option value="pulrocne">Půlročně</option>
                        <option value="rocne">Ročně</option>
                      </select>
                    </div>
                  ) : (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ ...softBlockStyle, marginTop: 4 }}>
                        <div style={{ fontWeight: 700, marginBottom: 10, color: "#0f172a" }}>Balíček termínů</div>
                        <div style={{ fontSize: 13, color: "#475569", marginBottom: 12 }}>
                          Vyber jen ty svátky a termíny, které si klient opravdu objednal.
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          <label style={checkboxRowStyle}>
                            <input
                              type="checkbox"
                              checked={!!form.balicekVelikonoce}
                              onChange={(e) => setForm({ ...form, balicekVelikonoce: e.target.checked })}
                            />
                            Velikonoce
                          </label>

                          <label style={checkboxRowStyle}>
                            <input
                              type="checkbox"
                              checked={!!form.balicekDusicky}
                              onChange={(e) => setForm({ ...form, balicekDusicky: e.target.checked })}
                            />
                            Dušičky
                          </label>

                          <label style={checkboxRowStyle}>
                            <input
                              type="checkbox"
                              checked={!!form.balicekVanoce}
                              onChange={(e) => setForm({ ...form, balicekVanoce: e.target.checked })}
                            />
                            Vánoce
                          </label>

                          <label style={checkboxRowStyle}>
                            <input
                              type="checkbox"
                              checked={!!form.balicekVyrociUmrti}
                              onChange={(e) => setForm({ ...form, balicekVyrociUmrti: e.target.checked })}
                            />
                            Výročí úmrtí
                          </label>

                          <label style={checkboxRowStyle}>
                            <input
                              type="checkbox"
                              checked={!!form.balicekNarozeniny}
                              onChange={(e) => setForm({ ...form, balicekNarozeniny: e.target.checked })}
                            />
                            Narozeniny
                          </label>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          <div>
                            <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum úmrtí pro výročí</div>
                            <input
                              style={inputStyle}
                              type="date"
                              value={form.balicekDatumUmrti || ""}
                              onChange={(e) => setForm({ ...form, balicekDatumUmrti: e.target.value })}
                            />
                          </div>

                          <div>
                            <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum narození</div>
                            <input
                              style={inputStyle}
                              type="date"
                              value={form.balicekDatumNarozeni || ""}
                              onChange={(e) => setForm({ ...form, balicekDatumNarozeni: e.target.value })}
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            alignItems: "end",
                            marginBottom: 12,
                          }}
                        >
                          <div style={{ minWidth: 220 }}>
                            <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Vlastní datum</div>
                            <input
                              style={inputStyle}
                              type="date"
                              value={form.noveBalicekDatum || ""}
                              onChange={(e) => setForm({ ...form, noveBalicekDatum: e.target.value })}
                            />
                          </div>

                          <button
                            type="button"
                            style={primaryButtonStyle}
                            onClick={() => {
                              const x = String(form.noveBalicekDatum || "").trim();
                              if (!x) return;
                              const arr = Array.isArray(form.balicekVlastniData) ? form.balicekVlastniData : [];
                              if (arr.includes(x)) return;
                              setForm({
                                ...form,
                                balicekVlastniData: [...arr, x].sort(),
                                noveBalicekDatum: "",
                              });
                            }}
                          >
                            Přidat datum
                          </button>
                        </div>

                        {!!(form.balicekVlastniData && form.balicekVlastniData.length) && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 12, marginBottom: 6, color: "#475569" }}>Vlastní termíny</div>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {form.balicekVlastniData.map((d) => (
                                <span key={d} style={chipBaseStyle}>
                                  {d}
                                  <button
                                    type="button"
                                    style={{
                                      marginLeft: 8,
                                      border: "none",
                                      background: "transparent",
                                      cursor: "pointer",
                                      color: "#b91c1c",
                                      fontWeight: 700,
                                    }}
                                    onClick={() =>
                                      setForm({
                                        ...form,
                                        balicekVlastniData: form.balicekVlastniData.filter((x) => x !== d),
                                      })
                                    }
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  <div />
                )}

                {!isJednorazova && !isBalicek ? (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum od</div>
                    <input
                      style={inputStyle}
                      type="date"
                      value={form.datumOd || ""}
                      onChange={(e) => setForm({ ...form, datumOd: e.target.value })}
                    />
                  </div>
                ) : (
                  <div />
                )}

                {!isJednorazova && !isBalicek ? (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Datum do</div>
                    <input
                      style={inputStyle}
                      type="date"
                      value={form.datumDo || ""}
                      onChange={(e) => setForm({ ...form, datumDo: e.target.value })}
                    />
                  </div>
                ) : (
                  <div />
                )}

                {isPredplatne ? (
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Předplatné uhrazeno do</div>
                    <input
                      style={inputStyle}
                      type="date"
                      value={form.uhrazenoDo || ""}
                      onChange={(e) => setForm({ ...form, uhrazenoDo: e.target.value })}
                    />
                  </div>
                ) : (
                  <div />
                )}

                {!isJednorazova ? (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 12, marginBottom: 6, color: "#475569" }}>
                      {isBalicek ? "Vybrané termíny balíčku" : "Vygenerované termíny návštěv"}
                    </div>
                    <div
                      style={{
                        ...softBlockStyle,
                        background: generatedVisitDates.length ? "#ffffff" : "#fff7ed",
                        border: generatedVisitDates.length ? "1px solid #e5e7eb" : "1px solid #fdba74",
                        color: generatedVisitDates.length ? "#0f172a" : "#9a3412",
                        fontWeight: generatedVisitDates.length ? 500 : 700,
                      }}
                    >
                      {generatedVisitDates.length
                        ? generatedVisitDates.join(", ")
                        : (isBalicek
                            ? "U balíčku zatím není vybraný žádný svátek ani vlastní datum."
                            : "Zatím nevychází žádný termín. Zkontroluj periodicitu a rozmezí Datum od / Datum do.")}
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 12,
                        marginTop: 14,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, marginBottom: 6, color: "#475569", fontWeight: 600 }}>
                          Květiny
                        </div>
                        <select
                          value={String(form.kvetinyLimit || "bez")}
                          onChange={(e) => {
                            setForm((prev) => applyBalicekVisualSelection(prev, "kvetiny", e.target.value));
                          }}
                          style={inputStyle}
                        >
                          <option value="bez">Bez květin</option>
                          <option value="do_200">Do 200 Kč</option>
                          <option value="do_300">Do 300 Kč</option>
                          <option value="do_500">Do 500 Kč</option>
                          <option value="vlastni">Vlastní částka</option>
                        </select>

                        {String(form.kvetinyLimit || "") === "vlastni" ? (
                          <input
                            style={{ ...inputStyle, marginTop: 8 }}
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Vlastní částka za květiny v Kč"
                            value={form.kvetinyVlastniCastka || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((prev) => {
                                const next = { ...prev, kvetinyVlastniCastka: value };
                                return applyBalicekVisualSelection(next, "kvetiny", "vlastni");
                              });
                            }}
                          />
                        ) : null}
                      </div>

                      <div>
                        <div style={{ fontSize: 12, marginBottom: 6, color: "#475569", fontWeight: 600 }}>
                          Dekorace
                        </div>
                        <select
                          value={String(form.dekoraceLimit || "bez")}
                          onChange={(e) => {
                            setForm((prev) => applyBalicekVisualSelection(prev, "dekorace", e.target.value));
                          }}
                          style={inputStyle}
                        >
                          <option value="bez">Bez dekorace</option>
                          <option value="do_100">Do 100 Kč</option>
                          <option value="do_200">Do 200 Kč</option>
                          <option value="do_300">Do 300 Kč</option>
                          <option value="vlastni">Vlastní částka</option>
                        </select>

                        {String(form.dekoraceLimit || "") === "vlastni" ? (
                          <input
                            style={{ ...inputStyle, marginTop: 8 }}
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Vlastní částka za dekorace v Kč"
                            value={form.dekoraceVlastniCastka || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((prev) => {
                                const next = { ...prev, dekoraceVlastniCastka: value };
                                return applyBalicekVisualSelection(next, "dekorace", "vlastni");
                              });
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div />
                )}

                {!isJednorazova ? (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 12, marginBottom: 6, color: "#475569" }}>Vygenerované termíny návštěv</div>
                    <div style={{ ...softBlockStyle, background: "#ffffff" }}>
                      {generatedVisitDates.length ? generatedVisitDates.join(", ") : "Zatím nejsou spočítané žádné termíny."}
                    </div>
                  </div>
                ) : (
                  <div />
                )}

              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={!!form.opakovana}
                  onChange={(e) => setForm({ ...form, opakovana: e.target.checked })}
                />
                Opakovaná služba
              </label>

              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={!!form.automatickyZapsatDoKalendare}
                  onChange={(e) => setForm({ ...form, automatickyZapsatDoKalendare: e.target.checked })}
                />
                Po uložení zapsat do kalendáře
              </label>
            </div>
          </div>
        </div>

          <div style={{ ...sectionCardStyle, ...softBlockStyle }}>
            <div style={sectionTitleStyle}>Finance zakázky</div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Cena celkem (Kč)</div>
                <input
                  style={{ ...inputStyle, background: "#f8fafc", color: "#0f172a" }}
                  type="number"
                  min="0"
                  step="0.01"
                  value={cenaCelkemNum}
                  readOnly
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Záloha (Kč)</div>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.zalohaCastka ?? 0}
                  onChange={(e) => setForm({ ...form, zalohaCastka: e.target.value, uhrazenoCelkem: e.target.value })}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Uhrazeno celkem (Kč)</div>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.uhrazenoCelkem ?? 0}
                  onChange={(e) => setForm({ ...form, uhrazenoCelkem: e.target.value })}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Zbývá doplatit (Kč)</div>
                <input
                  style={{ ...inputStyle, background: "#f8fafc", color: "#0f172a" }}
                  type="number"
                  value={doplatekCastkaNum}
                  readOnly
                />
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={chipBaseStyle}>Cena: <strong>{cenaCelkemNum.toLocaleString("cs-CZ")} Kč</strong></span>
              <span style={chipBaseStyle}>Uhrazeno: <strong>{uhrazenoCelkemNum.toLocaleString("cs-CZ")} Kč</strong></span>
              <span style={chipBaseStyle}>Doplatek: <strong>{doplatekCastkaNum.toLocaleString("cs-CZ")} Kč</strong></span>
              <span style={financniStavBadgeStyle(financniStavAuto)}>
                {financniStavAuto === "zaplaceno" ? "Zaplaceno" : (financniStavAuto === "zalohovano" ? "Zálohováno" : "Nezaplaceno")}
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginTop: 16 }}>
            <div style={sectionCardStyle}>
              <div style={sectionTitleStyle}>Zadání práce</div>
              <textarea
                style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
                placeholder="Zadání práce"
                value={form.zadaniPrace}
                onChange={(e) => setForm({ ...form, zadaniPrace: e.target.value })}
              />
            </div>

            <div style={sectionCardStyle}>
              <div style={sectionTitleStyle}>Poznámka</div>
              <textarea
                style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
                placeholder="Poznámka"
                value={form.poznamka}
                onChange={(e) => setForm({ ...form, poznamka: e.target.value })}
              />
            </div>
          </div>

          <div style={{ ...sectionCardStyle, marginTop: 16 }}>
            <div style={sectionTitleStyle}>Výběr služeb z ceníku</div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>Hlavní služba</div>
              {!String(form.typPece || "").trim() ? (
                <div style={{ ...softBlockStyle, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontWeight: 700 }}>
                  Nejprve vyber typ péče.
                </div>
              ) : (isJednorazova && !String(form.jednorazovyRezim || "").trim()) ? (
                <div style={{ ...softBlockStyle, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontWeight: 700 }}>
                  U jednorázové péče nejprve vyber režim: čištění nebo obnova.
                </div>
              ) : !hlavniSluzby.length ? (
                <div style={{ ...softBlockStyle, background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", fontWeight: 700 }}>
                  Pro zvolený typ péče zatím není k dispozici hlavní služba.
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {hlavniSluzby.map((it) => {
                    const active = String(form.hlavniSluzbaId || "") === String(it.id || "");
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => setMainService(it)}
                        style={{
                          ...primaryButtonStyle,
                          background: active ? "#0f172a" : "#ffffff",
                          color: active ? "#ffffff" : "#0f172a",
                          border: active ? "1px solid #0f172a" : "1px solid #cbd5e1",
                        }}
                      >
                        {it.nazev} ({Number(it.cenaBezDph || 0)} Kč)
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>Doplňkové služby</div>
              {!String(form.hlavniSluzbaId || "").trim() ? (
                <div style={{ ...softBlockStyle, background: "#ffffff" }}>
                  Nejdřív vyber hlavní službu.
                </div>
              ) : !doplnkoveSluzby.length ? (
                <div style={{ ...softBlockStyle, background: "#ffffff" }}>
                  Zatím nejsou k dispozici žádné doplňkové služby.
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {doplnkoveSluzby.map((it) => {
                    const active = (Array.isArray(form.items) ? form.items : []).some(
                      (x) => String(x?.cenikId || "") === String(it.id || "")
                    );
                    return (
                      <button
                        key={it.id}
                        type="button"
                        onClick={() => toggleDoplnkova(it)}
                        style={{
                          ...primaryButtonStyle,
                          background: active ? "#dbeafe" : "#ffffff",
                          color: "#0f172a",
                          border: active ? "1px solid #60a5fa" : "1px solid #cbd5e1",
                        }}
                      >
                        {it.nazev} ({Number(it.cenaBezDph || 0)} Kč)
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ ...sectionCardStyle, marginTop: 16 }}>
            <div style={sectionTitleStyle}>Zvolené položky</div>

            {mainServiceItem ? (
              <div style={{ ...softBlockStyle, marginBottom: 10, background: "#f8fafc" }}>
                <strong>Hlavní služba:</strong> {mainServiceItem.nazev}
              </div>
              ) : null}

            {!form.items.length && <div style={{ color: "#6b7280" }}>Zatím nic vybráno.</div>}

            {form.items.map((it) => (
              <div
                key={it.id}
                style={{
                  ...softBlockStyle,
                  marginBottom: 10,
                  background: "#ffffff",
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 8, color: "#111827" }}>{it.nazev}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 120px", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Množství</div>
                    <input
                      style={inputStyle}
                      type="number"
                      min="1"
                      value={it.mnozstvi}
                      onChange={(e) => updateItem(it.id, { mnozstvi: Number(e.target.value || 1) })}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, marginBottom: 4, color: "#475569" }}>Cena bez DPH</div>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      value={it.cenaBezDph}
                      onChange={(e) => updateItem(it.id, { cenaBezDph: Number(e.target.value || 0) })}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "end" }}>
                    <button type="button" onClick={() => removeItem(it.id)} style={{ ...primaryButtonStyle, width: "100%" }}>Smazat</button>
                  </div>
                </div>

                  {String(it.cenikId || "") !== String(form.hlavniSluzbaId || "") ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12, marginBottom: 6, color: "#475569" }}>Účtovat</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                        {[
                          ["jednou", "Jen jednou"],
                          ["kazda_navsteva", "Při každé návštěvě"],
                          ["vybrane_navstevy", "Jen při vybraných návštěvách"],
                        ].map(([value, label]) => {
                          const active = String(it.rezimUctovani || "jednou") === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              onClick={() => updateItem(it.id, {
                                rezimUctovani: value,
                                vybraneNavstevy: value === "vybrane_navstevy"
                                  ? (Array.isArray(it.vybraneNavstevy) ? it.vybraneNavstevy : [])
                                  : [],
                              })}
                              style={{
                                ...primaryButtonStyle,
                                background: active ? "#0f172a" : "#ffffff",
                                color: active ? "#ffffff" : "#0f172a",
                                border: active ? "1px solid #0f172a" : "1px solid #cbd5e1",
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>

                      {!isJednorazova && String(it.rezimUctovani || "jednou") === "vybrane_navstevy" ? (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12, marginBottom: 6, color: "#475569" }}>Vyber návštěvy</div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {generatedVisitDates.map((d) => {
                              const checked = Array.isArray(it.vybraneNavstevy) && it.vybraneNavstevy.includes(d);
                              return (
                                <label key={d} style={checkboxRowStyle}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      const arr = Array.isArray(it.vybraneNavstevy) ? it.vybraneNavstevy : [];
                                      updateItem(it.id, {
                                        vybraneNavstevy: e.target.checked
                                          ? [...arr, d]
                                          : arr.filter((x) => x !== d),
                                      });
                                    }}
                                  />
                                  {new Date(d).toLocaleDateString("cs-CZ")}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}

                      <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                        Provede se <strong>{getExecCountForItem(it)}×</strong>
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 10, fontSize: 13, color: "#334155", fontWeight: 600 }}>
                      Provede se <strong>{visitMultiplier}×</strong>
                    </div>
                  )}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginTop: 16 }}>
            <div style={sectionCardStyle}>
              <div style={sectionTitleStyle}>Fotodokumentace před</div>
              <input
                ref={predRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={onPickPred}
              />
              {uploadingPred ? <div style={{ marginTop: 8 }}>Nahrávám foto před…</div> : null}
              <div style={galleryWrapStyle}>
                {(form.fotoPred || []).map((url) => (
                  <div key={url} style={galleryCardStyle}>
                    <img src={url} alt="" style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8, display: "block" }} />
                    <button type="button" onClick={() => removeFoto("fotoPred", url)} style={{ ...primaryButtonStyle, marginTop: 8, width: "100%" }}>
                      Smazat
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div style={sectionCardStyle}>
              <div style={sectionTitleStyle}>Fotodokumentace po</div>
              <input
                ref={poRef}

                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={onPickPo}
              />
              {uploadingPo ? <div style={{ marginTop: 8 }}>Nahrávám foto po…</div> : null}
              <div style={galleryWrapStyle}>
                {(form.fotoPo || []).map((url) => (
                  <div key={url} style={galleryCardStyle}>
                    <img src={url} alt="" style={{ width: 110, height: 110, objectFit: "cover", borderRadius: 8, display: "block" }} />
                    <button type="button" onClick={() => removeFoto("fotoPo", url)} style={{ ...primaryButtonStyle, marginTop: 8, width: "100%" }}>
                      Smazat
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

            <div style={{ ...sectionCardStyle, marginTop: 16, background: "#fffbeb", border: "1px solid #fde68a" }}>
              <div style={{ ...sectionTitleStyle, marginBottom: 10, color: "#92400e" }}>Shrnutí před uložením</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                <div style={softBlockStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Typ zakázky</div>
                  <div style={{ fontWeight: 700, color: "#111827" }}>{workflowSummaryType}</div>
                </div>
                <div style={softBlockStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Hlavní služba</div>
                  <div style={{ fontWeight: 700, color: "#111827" }}>{mainServiceItem ? mainServiceItem.nazev : "-"}</div>
                </div>
                <div style={softBlockStyle}>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Nejbližší termín</div>
                  <div style={{ fontWeight: 600 }}>{nextPlanDate ? new Date(nextPlanDate).toLocaleDateString("cs-CZ") : "-"}</div>

                  {!isJednorazova ? (
                    <>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 10, marginBottom: 4 }}>Všechny termíny návštěv</div>
                      <div style={{ fontWeight: 600 }}>
                        {generatedVisitDates.length
                          ? generatedVisitDates.map((d) => new Date(d).toLocaleDateString("cs-CZ")).join(", ")
                          : "-"}
                      </div>
                    </>
                  ) : null}

                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Počet návštěv</div>
                  <div style={{ fontWeight: 600 }}>{generatedVisitDates.length}</div>

                  {isBalicek ? (
                    <>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 10, marginBottom: 4 }}>Balíček obsahuje</div>
                      <div style={{ fontWeight: 600 }}>
                        4 návštěvy v ceně{balicekExtraVisits > 0 ? `, navíc ${balicekExtraVisits} extra` : ""}
                      </div>

                      <div style={{ marginTop: 8, fontSize: 14, color: "#475569" }}>
                        Květiny: {String(form.kvetinyLimit || "") === "vlastni"
                          ? `${Number(form.kvetinyVlastniCastka || 0).toLocaleString("cs-CZ")} Kč`
                          : (Number(form.kvetinyCastka || 0) > 0 ? `do ${Number(form.kvetinyCastka || 0).toLocaleString("cs-CZ")} Kč` : "bez")}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 14, color: "#475569" }}>
                        Dekorace: {String(form.dekoraceLimit || "") === "vlastni"
                          ? `${Number(form.dekoraceVlastniCastka || 0).toLocaleString("cs-CZ")} Kč`
                          : (Number(form.dekoraceCastka || 0) > 0 ? `do ${Number(form.dekoraceCastka || 0).toLocaleString("cs-CZ")} Kč` : "bez")}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={chipBaseStyle}>Cena: <strong>{cenaCelkemNum.toLocaleString("cs-CZ")} Kč</strong></span>
                <span style={chipBaseStyle}>Doplatek: <strong>{doplatekCastkaNum.toLocaleString("cs-CZ")} Kč</strong></span>
                <span style={financniStavBadgeStyle(financniStavAuto)}>
                  {financniStavAuto === "zaplaceno" ? "Zaplaceno" : (financniStavAuto === "zalohovano" ? "Zálohováno" : "Nezaplaceno")}
                </span>
              </div>

              <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 12, background: "#ffffff", border: "1px solid #fde68a", color: "#78350f", fontSize: 13, fontWeight: 700, lineHeight: 1.45 }}>
                {workflowSummaryNext}
                {form.automatickyZapsatDoKalendare ? " Po uložení se zakázka zároveň zapíše do kalendáře." : ""}
              </div>
            </div>

          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setForm(getEmptyForm())}
              disabled={saving}
              style={{ ...primaryButtonStyle, opacity: saving ? 0.6 : 1 }}
            >
              Vyčistit formulář
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{ ...primaryButtonStyle, padding: "12px 18px", fontWeight: 700, opacity: saving ? 0.6 : 1 }}
            >
              {saving ? "Ukládám..." : "Uložit zakázku"}
            </button>
          </div>
        </div>
      );
  }
